import { useState, useEffect, useCallback } from 'react';
import { usersApi } from '@infrastructure/api';
export function useUsers() {
    const [users, setUsers] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [usersData, invitationsData] = await Promise.all([usersApi.list(), usersApi.listInvitations()]);
            setUsers(usersData);
            setInvitations(invitationsData);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load users';
            setError(message);
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    const inviteUser = useCallback(async (email, prototypeIds) => {
        const invitation = await usersApi.invite({ email, prototypeIds });
        setInvitations((prev) => [...prev, invitation]);
        return invitation;
    }, []);
    const getUserAccess = useCallback(async (userId) => {
        const detail = await usersApi.get(userId);
        return detail.accessiblePrototypeIds;
    }, []);
    const updateUserAccess = useCallback(async (userId, prototypeIds) => {
        const updatedUser = await usersApi.updateAccess(userId, { prototypeIds });
        setUsers((prev) => prev.map((u) => (u.id === userId ? updatedUser : u)));
    }, []);
    const deactivateUser = useCallback(async (userId) => {
        await usersApi.deactivate(userId);
        setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, isActive: false } : u)));
    }, []);
    const reactivateUser = useCallback(async (userId) => {
        const updatedUser = await usersApi.reactivate(userId);
        setUsers((prev) => prev.map((u) => (u.id === userId ? updatedUser : u)));
    }, []);
    const updateUserRole = useCallback(async (userId, role) => {
        const updatedUser = await usersApi.updateRole(userId, { role });
        setUsers((prev) => prev.map((u) => (u.id === userId ? updatedUser : u)));
    }, []);
    const deleteUser = useCallback(async (userId) => {
        await usersApi.delete(userId);
        setUsers((prev) => prev.filter((u) => u.id !== userId));
    }, []);
    const revokeInvitation = useCallback(async (invitationId) => {
        await usersApi.revokeInvitation(invitationId);
        setInvitations((prev) => prev.map((i) => (i.id === invitationId ? { ...i, status: 'revoked' } : i)));
    }, []);
    return {
        users,
        invitations,
        isLoading,
        error,
        refetch: fetchData,
        inviteUser,
        getUserAccess,
        updateUserAccess,
        deactivateUser,
        reactivateUser,
        updateUserRole,
        deleteUser,
        revokeInvitation,
    };
}
